import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, docsApi, type DocNavItem, type DocPage } from '../lib/api'

// DocsPage — documentation viewer used by BOTH the tenant app (via /docs) and
// the superadmin portal (embedded with embedded=true). It renders the nav tree
// on the left and Markdown content on the right. Markdown is rendered by a
// small dependency-free renderer (no react-markdown in this project).

// ── Minimal, safe Markdown → HTML ────────────────────────────────────────────
// Supports: #/##/### headings, bold, inline code, fenced code blocks, tables,
// unordered lists, blockquotes, paragraphs, and links. Input is trusted (it
// comes from our own docs DB, authored by staff), but we still escape HTML in
// text nodes to avoid accidental injection.
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Must escape quotes too: inline() interpolates escaped text into href="..."
    // and title attributes, so an unescaped " would break out of the attribute.
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
function inline(s: string): string {
  // escape first, then re-introduce our own safe tags
  let t = escapeHtml(s)
  t = t.replace(/`([^`]+)`/g, '<code>$1</code>')
  t = t.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  t = t.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, text, url) => {
    // Only allow safe schemes — block javascript:, data:, vbscript: etc.
    const safe = /^(https?:\/\/|\/|#|mailto:)/i.test(url) ? url : '#'
    return `<a href="${safe}" target="_blank" rel="noreferrer noopener">${text}</a>`
  })
  return t
}
function renderMarkdown(md: string): string {
  const lines = md.replace(/\r\n/g, '\n').split('\n')
  const out: string[] = []
  let i = 0
  let inList = false
  const closeList = () => { if (inList) { out.push('</ul>'); inList = false } }

  while (i < lines.length) {
    const line = lines[i]

    // Fenced code block ``` or ~~~
    const fence = line.match(/^\s*(```|~~~)/)
    if (fence) {
      closeList()
      const marker = fence[1]
      const body: string[] = []
      i++
      while (i < lines.length && !lines[i].includes(marker)) { body.push(lines[i]); i++ }
      i++ // skip closing fence
      out.push('<pre><code>' + escapeHtml(body.join('\n')) + '</code></pre>')
      continue
    }

    // Table block: header row | --- | rows
    if (line.includes('|') && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1]) && lines[i + 1].includes('-')) {
      closeList()
      const cells = (row: string) => row.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(c => c.trim())
      const header = cells(line)
      i += 2
      const rows: string[][] = []
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') { rows.push(cells(lines[i])); i++ }
      let tbl = '<table><thead><tr>' + header.map(h => `<th>${inline(h)}</th>`).join('') + '</tr></thead><tbody>'
      for (const r of rows) tbl += '<tr>' + r.map(c => `<td>${inline(c)}</td>`).join('') + '</tr>'
      tbl += '</tbody></table>'
      out.push(tbl)
      continue
    }

    // Headings
    const h = line.match(/^(#{1,4})\s+(.*)$/)
    if (h) { closeList(); const lvl = h[1].length; out.push(`<h${lvl}>${inline(h[2])}</h${lvl}>`); i++; continue }

    // Blockquote
    if (/^\s*>\s?/.test(line)) { closeList(); out.push('<blockquote>' + inline(line.replace(/^\s*>\s?/, '')) + '</blockquote>'); i++; continue }

    // List item
    if (/^\s*[-*]\s+/.test(line)) {
      if (!inList) { out.push('<ul>'); inList = true }
      out.push('<li>' + inline(line.replace(/^\s*[-*]\s+/, '')) + '</li>')
      i++; continue
    }

    // Blank line
    if (line.trim() === '') { closeList(); i++; continue }

    // Paragraph (accumulate consecutive non-empty, non-special lines)
    closeList()
    const para: string[] = [line]
    i++
    while (i < lines.length && lines[i].trim() !== '' && !/^(#{1,4}\s|\s*[-*]\s|\s*>\s?|```|~~~)/.test(lines[i]) && !lines[i].includes('|')) {
      para.push(lines[i]); i++
    }
    out.push('<p>' + inline(para.join(' ')) + '</p>')
  }
  closeList()
  return out.join('\n')
}

const SECTION_LABELS: Record<string, string> = {
  'user-guide': 'User guide',
  'admin': 'For administrators',
  'superadmin': 'Platform staff',
  'api-reference': 'API reference',
  'tenant-custom': 'Your farm',
}

export default function DocsPage({ embedded = false }: { embedded?: boolean }) {
  const [params, setParams] = useSearchParams()
  const [nav, setNav] = useState<DocNavItem[]>([])
  const [page, setPage] = useState<DocPage | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const slug = params.get('slug') || ''
  // Tenant subscription banner (skipped in the embedded superadmin portal).
  const [plan, setPlan] = useState('')
  const [addons, setAddons] = useState<string[]>([])

  useEffect(() => {
    docsApi.nav().then(r => setNav(r.data || [])).catch(() => setNav([]))
    if (embedded) return
    api.get('/billing/subscription').then((r) => {
      const s = (r.data || {}) as Record<string, unknown>
      setPlan(String(s.plan_name || s.plan || s.tier || '').trim())
    }).catch(() => {})
    api.get('/me/addons').then((r) => {
      const list: Array<Record<string, unknown>> = Array.isArray(r.data) ? r.data : (r.data?.addons ?? [])
      setAddons(list
        .filter((a) => (a.status ?? 'active') === 'active')
        .map((a) => String(a.label || a.addon_type || a.type || '').replace(/_/g, ' ').trim())
        .filter(Boolean))
    }).catch(() => {})
  }, [embedded])

  useEffect(() => {
    setLoading(true); setError('')
    const target = slug || (nav[0]?.slug ?? 'getting-started')
    docsApi.page(target)
      .then(r => setPage(r.data))
      .catch(() => setError('This page is not available.'))
      .finally(() => setLoading(false))
  }, [slug, nav])

  // Group nav items by section for the sidebar.
  const grouped = useMemo(() => {
    const flat: DocNavItem[] = []
    const walk = (items: DocNavItem[]) => items.forEach(it => { flat.push(it); if (it.children) walk(it.children) })
    walk(nav)
    const by: Record<string, DocNavItem[]> = {}
    for (const it of flat) (by[it.section] ??= []).push(it)
    return by
  }, [nav])

  const html = useMemo(() => (page ? renderMarkdown(page.content) : ''), [page])

  const select = (s: string) => setParams(prev => { const p = new URLSearchParams(prev); p.set('slug', s); return p })

  return (
    <div style={{ display: 'flex', gap: 0, minHeight: embedded ? 'auto' : 'calc(100dvh - 56px)' }}>
      {/* Sidebar nav */}
      <aside style={{
        width: 248, flexShrink: 0, borderRight: '0.5px solid var(--color-border-tertiary)',
        padding: '20px 16px', background: 'var(--color-background-primary)',
      }}>
        <h2 style={{ fontSize: 13, fontWeight: 600, margin: '0 0 12px', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Documentation
        </h2>
        {Object.keys(grouped).length === 0 && (
          <p style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>Loading…</p>
        )}
        {Object.entries(grouped).map(([section, items]) => (
          <div key={section} style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-tertiary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {SECTION_LABELS[section] ?? section}
            </div>
            {items.map(it => {
              const active = (slug || nav[0]?.slug) === it.slug
              const nested = it.parent_slug ? 14 : 0
              return (
                <button key={it.slug} onClick={() => select(it.slug)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', background: active ? 'var(--color-background-tertiary)' : 'none',
                    border: 'none', cursor: 'pointer', padding: '6px 8px', paddingLeft: 8 + nested,
                    borderRadius: 6, fontSize: 13, marginBottom: 1,
                    color: active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                    fontWeight: active ? 600 : 400,
                  }}>
                  {it.title}
                </button>
              )
            })}
          </div>
        ))}
      </aside>

      {/* Content */}
      <main id="main-content" style={{ flex: 1, padding: '28px 40px', maxWidth: 820, overflow: 'auto' }}>
        {!embedded && plan && (
          <div style={{ marginBottom: 20, padding: '11px 16px', borderRadius: 10, border: '1px solid var(--color-border)', background: 'var(--color-background-tertiary)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, fontSize: 13 }}>
            <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
              Your plan: {plan.charAt(0).toUpperCase() + plan.slice(1)}
            </span>
            {addons.length > 0 && (
              <span style={{ color: 'var(--color-text-secondary)' }}>· Add-ons: {addons.join(', ')}</span>
            )}
            <span style={{ flex: 1 }} />
            <button onClick={() => select('your-subscription')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-info)', fontWeight: 600, fontSize: 13, padding: 0 }}>
              What&apos;s included →
            </button>
          </div>
        )}
        {loading && <p style={{ color: 'var(--color-text-tertiary)' }} aria-busy="true">Loading…</p>}
        {error && !loading && (
          <div role="alert" style={{ fontSize: 14, color: 'var(--color-text-danger)' }}>{error}</div>
        )}
        {page && !loading && !error && (
          <article className="docs-content" dangerouslySetInnerHTML={{ __html: html }} />
        )}
      </main>
    </div>
  )
}
